import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import {
  ZefAuthApi,
  performWebAuthnAuthentication,
  SUDO_ERROR_KEY
} from '@zerops/zef/auth';
import {
  zefRemoveError,
  zefAddError,
  selectZefErrorsByType
} from '@zerops/zef/errors';
import {
  switchMap,
  map,
  catchError,
  mergeMap,
  withLatestFrom,
  tap,
  delay
} from 'rxjs/operators';
import { of, from } from 'rxjs';
import { sudoReauthActions } from './sudo-reauth.state';
import { SudoReauthForm } from './sudo-reauth.form';

const MAX_SUDO_RETRY_COUNT = 2;

@Injectable()
export class SudoReauthEffect {
  #actions$ = inject(Actions);
  #store = inject(Store);
  #api = inject(ZefAuthApi);
  #form = inject(SudoReauthForm);

  // # Password Authentication

  submitPassword$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(sudoReauthActions.submitPassword),
      switchMap((action) =>
        this.#api.sudoEnablePassword$(action.data.password).pipe(
          map(() => sudoReauthActions.submitPasswordSuccess(undefined, action)),
          catchError((error) => of(
            sudoReauthActions.submitPasswordFailure(error, action)
          ))
        )
      )
    )
  );

  onPasswordSuccess$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(sudoReauthActions.submitPasswordSuccess),
      withLatestFrom(
        this.#store.pipe(select(selectZefErrorsByType(SUDO_ERROR_KEY)))
      ),
      mergeMap(([ _, sudoErrors ]) => {
        const error = sudoErrors[0];
        const actions = [
          zefRemoveError(SUDO_ERROR_KEY)
        ];

        if (error?.originalAction) {
          const retryAction = this.#createRetryAction(error.originalAction);
          if (retryAction) {
            // Handle both single action and array of actions
            if (Array.isArray(retryAction)) {
              actions.push(...retryAction);
            } else {
              actions.push(retryAction);
            }
          }
        }

        return actions;
      })
    )
  );

  // # TOTP Authentication

  submitTOTP$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(sudoReauthActions.submitTOTP),
      switchMap((action) =>
        this.#api.sudoEnableTOTP$(action.data.token).pipe(
          map((response) => sudoReauthActions.submitTOTPSuccess(response, action)),
          catchError((error) => of(
            sudoReauthActions.submitTOTPFailure(error, action)
          ))
        )
      )
    )
  );

  onTOTPSuccess$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(sudoReauthActions.submitTOTPSuccess),
      withLatestFrom(
        this.#store.pipe(select(selectZefErrorsByType(SUDO_ERROR_KEY)))
      ),
      mergeMap(([ _, sudoErrors ]) => {
        const error = sudoErrors[0];
        const actions = [
          zefRemoveError(SUDO_ERROR_KEY)
        ];

        if (error?.originalAction) {
          const retryAction = this.#createRetryAction(error.originalAction);
          if (retryAction) {
            // Handle both single action and array of actions
            if (Array.isArray(retryAction)) {
              actions.push(...retryAction);
            } else {
              actions.push(retryAction);
            }
          }
        }

        return actions;
      })
    )
  );

  // # WebAuthn Authentication

  startWebAuthn$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(sudoReauthActions.startWebAuthn),
      switchMap((action) =>
        this.#api.twoFALoginStart$().pipe(
          map((response) => sudoReauthActions.startWebAuthnSuccess(response, action)),
          catchError((error) => of(
            sudoReauthActions.startWebAuthnFailure(error, action)
          ))
        )
      )
    )
  );

  onWebAuthnSuccess$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(sudoReauthActions.startWebAuthnSuccess),
      switchMap((action) =>
        performWebAuthnAuthentication({
          challenge: action.data.response,
          timeout: 60000
        }).pipe(
          map((authResponse) => sudoReauthActions.finishWebAuthn({
            data: {
              sessionId: action.data.sessionId,
              response: authResponse
            }
          })),
          catchError((error) => of(
            sudoReauthActions.startWebAuthnFailure(error, action)
          ))
        )
      )
    )
  );

  finishWebAuthn$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(sudoReauthActions.finishWebAuthn),
      switchMap((action) =>
        this.#api.sudoEnableWebAuthn$(action.data).pipe(
          map(() => sudoReauthActions.finishWebAuthnSuccess(undefined, action)),
          catchError((error) => of(
            sudoReauthActions.finishWebAuthnFailure(error, action)
          ))
        )
      )
    )
  );

  onWebAuthnFinishSuccess$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(sudoReauthActions.finishWebAuthnSuccess),
      withLatestFrom(
        this.#store.pipe(select(selectZefErrorsByType(SUDO_ERROR_KEY)))
      ),
      mergeMap(([ _, sudoErrors ]) => {
        const error = sudoErrors[0];
        const actions = [
          zefRemoveError(SUDO_ERROR_KEY)
        ];

        if (error?.originalAction) {
          const retryAction = this.#createRetryAction(error.originalAction);
          if (retryAction) {
            // Handle both single action and array of actions
            if (Array.isArray(retryAction)) {
              actions.push(...retryAction);
            } else {
              actions.push(retryAction);
            }
          }
        }

        return actions;
      })
    )
  );

  // # Cancel Flow

  onCancel$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(sudoReauthActions.cancel),
      mergeMap(() => [
        zefRemoveError(SUDO_ERROR_KEY)
      ])
    )
  );

  // # Component Lifecycle

  /**
   * Handles component destruction - aborts WebAuthn ceremony immediately
   */
  onComponentDestroyed$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(sudoReauthActions.componentDestroyed),
      switchMap(() => from(import('@simplewebauthn/browser')).pipe(
        tap(({ WebAuthnAbortService }) => {
          WebAuthnAbortService.cancelCeremony();
        })
      ))
    ),
    { dispatch: false }
  );

  /**
   * Delays form reset after component destruction to prevent FOUC.
   *
   * When the dialog closes (error removed), the component unmounts and triggers
   * componentDestroyed. We delay the form reset by 400ms to allow the dialog
   * close animation to complete before the form changes back to default values.
   */
  onComponentDestroyedFormReset$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(sudoReauthActions.componentDestroyed),
      delay(250),
      mergeMap(() => [
        this.#form.reset(),
        this.#form.setDefaultValues()
      ])
    )
  );

  // # Helper: Create Retry Action with Counter

  #createRetryAction(originalAction: any): any | any[] | null {
    // Guard: Ensure originalAction exists and is valid
    if (!originalAction || typeof originalAction !== 'object') {
      return null;
    }

    // Guard: Ensure originalAction has a type (required for NgRx actions)
    if (!originalAction.type) {
      return null;
    }

    const retryCount = originalAction.meta?.sudoRetryCount || 0;

    if (retryCount >= MAX_SUDO_RETRY_COUNT) {
      // Return array of actions: show snack error + close sudo dialog
      return [
        zefAddError(
          'sudo-max-retries',
          'SUDO_MAX_RETRIES',
          'Maximum sudo retry attempts reached. Please retry the original operation.',
          undefined,
          undefined,
          'snack'
        ),
        zefRemoveError(SUDO_ERROR_KEY)
      ];
    }

    return {
      ...originalAction,
      meta: {
        ...originalAction.meta,
        sudoRetryCount: retryCount + 1
      }
    };
  }
}
